import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PageForm from 'components/pageForm/PageForm';
import { apiErrorText, errorCodeOf } from 'helper/apiErrorText';
import { COMPANY_DELETE_REASONS } from 'constants/companyDeleteReasons';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';

// The list no longer matches the api: read it again rather than keep a stale row.
const STALE_ERROR_CODES = ['COMPANY_NOT_FOUND', 'COMPANY_NOT_ARCHIVED'];

// The agency is in Martinique: a company archived late in the evening there
// keeps that day, not the next one in UTC.
const archivedDay = (at) =>
  at
    ? new Date(at).toLocaleDateString('fr-FR', {
        timeZone: 'America/Martinique',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';

const HEADER_CELL =
  'bg-gray-50 py-3.5 px-2 text-left font-medium text-gray-700 first:pl-4 last:pr-4';

/**
 * « Paramètres · Entreprises archivées » of the admin console: the companies
 * archived from their record (admin/crm/company/archived, the latest first),
 * each with « Restaurer » (admin/crm/company/restore), which takes it out of
 * the list.
 */
const AdminArchivedCompanies = () => {
  const { text } = useText('adminConsole');
  const { text: recordText } = useText('record');
  const { text: formText, language } = useText('recordForm');
  const { call, ready } = useCrmContext();
  const [companies, setCompanies] = useState(null);
  const [failed, setFailed] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(() => {
    setFailed(false);
    call(crmApi.getArchivedCompanies).then(setCompanies, () => setFailed(true));
  }, [call]);
  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  const restore = (companyId) => {
    setRestoring(companyId);
    call(crmApi.restoreCompany, { companyId }).then(
      () => {
        setRestoring(null);
        setCompanies((current) => current.filter((one) => one.companyId !== companyId));
        toast.success(text('archivedRestored'));
      },
      (error) => {
        setRestoring(null);
        toast.error(apiErrorText(error, { language, text, group: 'archivedRestoreErrors' }));
        if (STALE_ERROR_CODES.includes(errorCodeOf(error))) load();
      },
    );
  };

  const reasonLabel = (reason) =>
    COMPANY_DELETE_REASONS.includes(reason) ? formText(`deleteReason_${reason}`) : reason || '—';

  return (
    <PageForm title={text('archivedPageTitle')}>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-6 lg:px-8">
        {failed && (
          <CompactEmptyState
            title={text('archivedLoadError')}
            action={{ label: text('goalsRetry'), onClick: load }}
          />
        )}
        {!failed && !companies && (
          <div className="h-40 animate-pulse rounded-lg bg-gray-100" aria-busy="true" />
        )}
        {!failed && companies && (
          <section className="w-full overflow-hidden rounded-lg border border-gray-100 bg-white">
            <div className="flex flex-col gap-0.5 px-4 py-4">
              <h2 className="text-base font-semibold text-gray-900">{text('archivedTitle')}</h2>
              <p className="text-sm text-gray-500">{text('archivedText')}</p>
            </div>
            {companies.length === 0 ? (
              <p className="border-t border-gray-100 px-4 py-6 text-center text-sm text-gray-500">
                {text('archivedNone')}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-gray-100">
                    <th className={HEADER_CELL}>{text('archivedCompany')}</th>
                    <th className={HEADER_CELL}>{text('archivedStage')}</th>
                    <th className={HEADER_CELL}>{text('archivedAt')}</th>
                    <th className={HEADER_CELL}>{text('archivedBy')}</th>
                    <th className={HEADER_CELL}>{text('archivedReason')}</th>
                    <th className={HEADER_CELL}>
                      <span className="sr-only">{text('archivedRestore')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => (
                    <tr key={company.companyId} className="border-b border-gray-50 last:border-b-0">
                      <td className="py-3 pl-4 pr-2">
                        {/* No link: the api answers 404 for an archived company's record. */}
                        <span className="font-medium uppercase text-gray-900">{company.name}</span>
                      </td>
                      <td className="px-2 py-3 text-gray-600">
                        {company.stage ? recordText(`stage_${company.stage}`) : '—'}
                      </td>
                      <td className="px-2 py-3 text-gray-600">{archivedDay(company.archivedAt)}</td>
                      <td className="px-2 py-3 text-gray-600">{company.archivedBy?.name || '—'}</td>
                      <td className="px-2 py-3 text-gray-600">
                        <span className="block">{reasonLabel(company.reason)}</span>
                        {company.details && (
                          <span className="block text-xs text-gray-500">{company.details}</span>
                        )}
                      </td>
                      <td className="py-3 pl-2 pr-4 text-right">
                        <Button
                          color={restoring ? 'DISABLED' : 'SECONDARY'}
                          size="SM"
                          disabled={Boolean(restoring)}
                          aria-label={text('archivedRestoreLabel', { name: company.name })}
                          onClick={() => restore(company.companyId)}
                        >
                          {text('archivedRestore')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}
      </div>
    </PageForm>
  );
};

export default AdminArchivedCompanies;
